/**
 * What: Converts Shopify micro-survey form drafts into the shared micro-survey payload contract.
 * Transport / data-flow: Pure browser helpers consumed by the Shopify embedded app before POSTing through `/api/micro-surveys`.
 *
 */
import {
  microSurveyPayloadSchema,
  psychographicSegmentValues,
  type MicroSurveyChoice,
  type MicroSurveyPayload,
  type MicroSurveyQuestion,
  type MicroSurveyTriggerCondition,
  type MicroSurveyTriggerType,
  type PsychographicSegment,
} from '@testfeed/survey-contracts'

type SupportedQuestionKind = 'choice_single' | 'visual_choice'
type RuntimeQuestionKind = SupportedQuestionKind | 'choice_multi' | 'short_text'
type ChoiceInterpretationType = 'purchase_barrier' | 'use_case' | 'psychographic_segment' | 'none'

export type MicroSurveyDraft = {
  triggerType: MicroSurveyTriggerType
  questionKind: RuntimeQuestionKind
  questionText: string
  choices: Array<{
    id: string
    label: string
    icon?: string
    interpretationType: ChoiceInterpretationType
    interpretationValue: string
  }>
}

export const supportedMicroSurveyTriggers = [
  {
    type: 'high_intent',
    label: 'High-intent product viewing',
    event: 'product_viewed',
  },
  {
    type: 'post_purchase',
    label: 'Post-purchase checkout completion',
    event: 'checkout_completed',
  },
] satisfies Array<{ type: MicroSurveyTriggerType; label: string; event: string }>

export const supportedQuestionKinds = [
  {
    value: 'choice_single',
    label: 'Single choice',
  },
  {
    value: 'visual_choice',
    label: 'Visual choice',
  },
] satisfies Array<{ value: SupportedQuestionKind; label: string }>

export type MicroSurveySaveBody = {
  status: 'active'
  payload: MicroSurveyPayload
}

function triggerFor(type: MicroSurveyTriggerType): MicroSurveyTriggerCondition | null {
  const trigger = supportedMicroSurveyTriggers.find((candidate) => candidate.type === type)
  if (!trigger) return null
  return {
    type: trigger.type,
    event: trigger.event,
  }
}

function normalizeToken(value: string, fallback: string) {
  const token = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return token || fallback
}

function normalizeChoiceId(value: string, index: number) {
  return normalizeToken(value, `choice_${index + 1}`)
}

function normalizeSurveyId(questionText: string, triggerType: MicroSurveyTriggerType) {
  return `micro_survey_${triggerType}_${normalizeToken(questionText, 'untitled')}`
}

function normalizeQuestionId(questionText: string) {
  return `question_${normalizeToken(questionText, 'primary')}`
}

function isSupportedQuestionKind(value: RuntimeQuestionKind): value is SupportedQuestionKind {
  return supportedQuestionKinds.some((kind) => kind.value === value)
}

function isPsychographicSegment(value: string): value is PsychographicSegment {
  return psychographicSegmentValues.some((segment) => segment === value)
}

function buildChoiceInterpretation(type: ChoiceInterpretationType, value: string): MicroSurveyChoice['interpretation'] {
  if (type === 'none') {
    return { type: 'none' }
  }

  if (type === 'psychographic_segment') {
    if (!isPsychographicSegment(value)) {
      return { type: 'none' }
    }
    return { type, value }
  }

  return {
    type,
    value: normalizeToken(value, type),
  }
}

/** Creates the local form shape used by the merchant-facing micro-survey authoring UI. */
export function createEmptyMicroSurveyDraft(overrides: Partial<MicroSurveyDraft> = {}): MicroSurveyDraft {
  return {
    triggerType: 'high_intent',
    questionKind: 'choice_single',
    questionText: 'What would help you decide?',
    choices: [
      {
        id: 'need_more_details',
        label: 'More product details',
        interpretationType: 'purchase_barrier',
        interpretationValue: 'needs_more_information',
      },
      {
        id: 'comparing_options',
        label: 'Comparing options',
        interpretationType: 'purchase_barrier',
        interpretationValue: 'comparing_options',
      },
    ],
    ...overrides,
  }
}

/** Reports merchant-fixable draft errors before the payload reaches the Shopify Worker. */
export function validateMicroSurveyDraft(draft: MicroSurveyDraft): string[] {
  const errors: string[] = []
  if (!triggerFor(draft.triggerType)) {
    errors.push('Choose a trigger supported by the current Web Pixel runtime.')
  }
  if (!isSupportedQuestionKind(draft.questionKind)) {
    errors.push('Choose a question type supported by the storefront widget.')
  }
  if (!draft.questionText.trim()) {
    errors.push('Enter the survey question.')
  }
  if (draft.choices.length < 2) {
    errors.push('Add at least two choices.')
  }

  draft.choices.forEach((choice, index) => {
    if (!choice.label.trim()) {
      errors.push(`Choice ${index + 1} needs a label.`)
    }
    if (choice.interpretationType !== 'none' && !choice.interpretationValue.trim()) {
      errors.push(`Choice ${index + 1} needs an interpretation value.`)
    }
    if (choice.interpretationType === 'psychographic_segment' && !isPsychographicSegment(choice.interpretationValue)) {
      errors.push(`Choice ${index + 1} needs a supported psychographic segment.`)
    }
  })

  return errors
}

function buildChoices(draft: MicroSurveyDraft): MicroSurveyChoice[] {
  return draft.choices.map((choice, index) => {
    const icon = choice.icon?.trim()
    return {
      id: normalizeChoiceId(choice.id || choice.label, index),
      label: choice.label.trim(),
      icon: icon || undefined,
      interpretation: buildChoiceInterpretation(choice.interpretationType, choice.interpretationValue),
    }
  })
}

function buildQuestion(draft: MicroSurveyDraft): MicroSurveyQuestion {
  return {
    id: normalizeQuestionId(draft.questionText),
    questionKind: isSupportedQuestionKind(draft.questionKind) ? draft.questionKind : 'choice_single',
    text: draft.questionText.trim(),
    choices: buildChoices(draft),
  }
}

/** Builds the existing Shopify admin route body without introducing a separate persisted UI schema. */
export function buildMicroSurveySaveBody(draft: MicroSurveyDraft): MicroSurveySaveBody {
  const errors = validateMicroSurveyDraft(draft)
  if (errors.length > 0) {
    throw new Error(errors.join(' '))
  }

  const triggerCondition = triggerFor(draft.triggerType)
  if (!triggerCondition) {
    throw new Error('Choose a trigger supported by the current Web Pixel runtime.')
  }

  const payload = microSurveyPayloadSchema.parse({
    schemaVersion: 'micro-survey-payload-v2',
    surveyId: normalizeSurveyId(draft.questionText, draft.triggerType),
    triggerCondition,
    placements: [triggerCondition],
    questions: [buildQuestion(draft)],
  })

  return {
    status: 'active',
    payload,
  }
}
